import React, { useState, useMemo } from 'react';
import Icon from '@mdi/react';
import { mdiTicketOutline, mdiAccountCashOutline, mdiCalendarCheck, mdiRefresh, mdiChevronDown, mdiSchool, mdiDownload, mdiArrowUp, mdiArrowDown, mdiCreditCardRefund, mdiCreditCardPlus, mdiCreditCardCheckOutline, mdiCreditCardSyncOutline } from '@mdi/js';
import DateInputWithPicker from '../DateInputWithPicker';
import { formatCurrencyFromMinor } from '../../utils/currency';
import './TransactionsTable.css';

const STATUS_CONFIG = {
  succeeded: { label: 'Paid', colorClass: 'status-green' },
  pending: { label: 'Pending', colorClass: 'status-orange' },
  failed: { label: 'Failed', colorClass: 'status-red' },
  abandoned: { label: 'Abandoned', colorClass: 'status-gray' },
};

const PAGE_SIZE = 20;

/**
 * TransactionsTable — Displays transaction history with type/status/date filters,
 * payout date column, totals row, and pagination.
 */
function TransactionsTable({ transactions, organisationId, onRefresh }) {
  const [filters, setFilters] = useState({ type: 'all', status: 'all', dateFrom: '', dateTo: '' });
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getPayoutDate = (txn) => {
    if (txn.payoutStatus === 'paid' && txn.actualPayoutDate) {
      return formatShortDate(txn.actualPayoutDate);
    }
    if (txn.payoutStatus === 'scheduled' && txn.scheduledPayoutDate) {
      return formatShortDate(txn.scheduledPayoutDate);
    }
    return '—';
  };

  // External (paid-outside-the-platform) rows badge GREEN — paid is paid, any rail. The accounting
  // axis (reconciled vs not) is shown by the icon only, never an "action required" treatment.
  const StatusBadge = ({ txn }) => {
    if (txn.paidOutsidePlatform === true) {
      const reconciled = !!txn.reconciledAt;
      return (
        <span className="txn-status-badge status-external">
          <Icon path={reconciled ? mdiCreditCardCheckOutline : mdiCreditCardSyncOutline} size={0.6} />
          {reconciled ? 'Paid · reconciled' : 'Paid · unreconciled'}
        </span>
      );
    }
    const config = STATUS_CONFIG[txn.status] || { label: txn.status, colorClass: 'status-gray' };
    return <span className={`txn-status-badge ${config.colorClass}`}>{config.label}</span>;
  };

  const TypeIcon = ({ type }) => {
    if (type === 'ticket_purchase') return (
      <span className="txn-type-badge type-ticket">
        <Icon path={mdiTicketOutline} size={0.7} /><span>Ticket</span>
      </span>
    );
    if (type === 'class_payment') return (
      <span className="txn-type-badge type-class">
        <Icon path={mdiSchool} size={0.7} /><span>Class</span>
      </span>
    );
    if (type === 'class_adjustment') return (
      <span className="txn-type-badge type-class">
        <Icon path={mdiCreditCardPlus} size={0.7} /><span>Adjustment</span>
      </span>
    );
    if (type === 'booking_refund' || type === 'class_refund') return (
      <span className="txn-type-badge type-refund">
        <Icon path={mdiCreditCardRefund} size={0.7} /><span>Refund</span>
      </span>
    );
    if (type === 'booking_credit_grant') return (
      <span className="txn-type-badge type-credit">
        <Icon path={mdiCreditCardPlus} size={0.7} /><span>Credit</span>
      </span>
    );
    return (
      <span className="txn-type-badge type-booking">
        <Icon path={mdiCalendarCheck} size={0.7} /><span>Private</span>
      </span>
    );
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const filtered = useMemo(() => {
    const result = transactions.filter((txn) => {
      const typeMatch = filters.type === 'all' || txn.type === filters.type;
      const statusMatch = filters.status === 'all' || txn.status === filters.status;
      let dateMatch = true;
      if (filters.dateFrom) {
        dateMatch = dateMatch && new Date(txn.createdAt) >= new Date(filters.dateFrom);
      }
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        dateMatch = dateMatch && new Date(txn.createdAt) <= toDate;
      }
      return typeMatch && statusMatch && dateMatch;
    });
    result.sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'createdAt') {
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
      } else if (sortField === 'amount') {
        aVal = a.amounts?.instructorReceives || 0;
        bVal = b.amounts?.instructorReceives || 0;
      } else if (sortField === 'name') {
        aVal = (a.customerName || '').toLowerCase();
        bVal = (b.customerName || '').toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [transactions, filters, sortField, sortDir]);

  // Abandoned rows (checkout opened, never paid, PaymentIntent cancelled) are listed but never
  // totalled — the count label and the amounts use this same set so they agree.
  const totalled = useMemo(() => filtered.filter((txn) => txn.status !== 'abandoned'), [filtered]);

  // Totals grouped BY CURRENCY — never sum across currencies (a travelling instructor's ledger
  // can hold more than one) and never label the total with one arbitrary transaction's currency.
  const totalsByCurrency = useMemo(() => {
    const m = new Map();
    for (const txn of totalled) {
      const cur = txn.amounts?.currency || 'AUD';
      m.set(cur, (m.get(cur) || 0) + (txn.amounts?.instructorReceives || 0));
    }
    return [...m.entries()].map(([currency, amount]) => ({ currency, amount }));
  }, [totalled]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const TYPE_LABELS = { ticket_purchase: 'Ticket', booking_payment: 'Private Lesson', class_payment: 'Class', class_adjustment: 'Adjustment', booking_refund: 'Refund', class_refund: 'Refund', booking_credit_grant: 'Credit' };

  const handleExportCsv = () => {
    if (!filtered.length) return;

    const headers = ['Name', 'Date Paid', 'Type', 'Amount', 'Status'];
    const rows = filtered.map(txn => [
      txn.customerName || 'Student',
      new Date(txn.createdAt).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
      TYPE_LABELS[txn.type] || txn.type,
      ((txn.amounts?.instructorReceives || 0) / 100).toFixed(2),
      txn.paidOutsidePlatform === true
        ? (txn.reconciledAt ? 'Paid (external, reconciled)' : 'Paid (external, unreconciled)')
        : (txn.status === 'succeeded' ? 'Paid' : txn.status === 'abandoned' ? 'Abandoned' : txn.status),
    ]);

    const csv = [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="transactions-table-container">
      <div className="txn-title-row">
        <h2 className="finances-section-title">Transactions</h2>
        <button onClick={handleExportCsv} className="txn-export-btn" disabled={!filtered.length} title="Export filtered transactions as CSV">
          <Icon path={mdiDownload} size={0.85} />
          <span>Export CSV</span>
        </button>
      </div>
      <div className="transactions-filters">
        <div className="txn-date-filters">
          <DateInputWithPicker
            value={filters.dateFrom}
            onChange={(isoDate) => handleFilterChange('dateFrom', isoDate)}
            placeholder="From"
            style={{ maxWidth: '140px' }}
          />
          <span className="txn-date-sep">—</span>
          <DateInputWithPicker
            value={filters.dateTo}
            onChange={(isoDate) => handleFilterChange('dateTo', isoDate)}
            placeholder="To"
            style={{ maxWidth: '140px' }}
          />
        </div>

        <div className="filter-select-wrapper">
          <select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className={`filter-select${filters.type !== 'all' ? ' filter-active' : ''}`}
          >
            <option value="all">All Types</option>
            <option value="ticket_purchase">Ticket Sales</option>
            <option value="booking_payment">Private Lessons</option>
            <option value="class_payment">Class Enrolments</option>
            <option value="class_adjustment">Adjustments</option>
            <option value="booking_refund">Refunds</option>
            <option value="booking_credit_grant">Credits</option>
          </select>
          <Icon path={mdiChevronDown} size={0.75} className="filter-select-icon" />
        </div>

        <div className="filter-select-wrapper">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className={`filter-select${filters.status !== 'all' ? ' filter-active' : ''}`}
          >
            <option value="all">All Status</option>
            <option value="succeeded">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="abandoned">Abandoned</option>
          </select>
          <Icon path={mdiChevronDown} size={0.75} className="filter-select-icon" />
        </div>

        <button onClick={onRefresh} className="txn-refresh-btn" title="Refresh transactions">
          <Icon path={mdiRefresh} size={0.85} />
          <span>Refresh</span>
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="transactions-empty">
          <Icon path={mdiAccountCashOutline} size={1.5} />
          <p>No transactions found</p>
        </div>
      ) : (
        <>
          <div className="transactions-scroll">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th className="txn-sortable-th" onClick={() => handleSort('name')}>
                    Name
                    {sortField === 'name' && <Icon path={sortDir === 'asc' ? mdiArrowUp : mdiArrowDown} size={0.55} className="txn-sort-icon" />}
                  </th>
                  <th className="txn-sortable-th" onClick={() => handleSort('createdAt')}>
                    Date Paid
                    {sortField === 'createdAt' && <Icon path={sortDir === 'asc' ? mdiArrowUp : mdiArrowDown} size={0.55} className="txn-sort-icon" />}
                  </th>
                  <th>Type</th>
                  <th className="txn-sortable-th" onClick={() => handleSort('amount')}>
                    Amount
                    {sortField === 'amount' && <Icon path={sortDir === 'asc' ? mdiArrowUp : mdiArrowDown} size={0.55} className="txn-sort-icon" />}
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((txn) => (
                  <tr key={txn.transactionId}>
                    <td className="txn-customer">{txn.customerName || 'Student'}</td>
                    <td className="txn-date">{formatDate(txn.createdAt)}</td>
                    <td><TypeIcon type={txn.type} /></td>
                    <td className="txn-receive">
                      {formatCurrencyFromMinor(txn.amounts?.instructorReceives, txn.amounts?.currency)}
                    </td>
                    <td><StatusBadge txn={txn} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="txn-totals-row">
                  <td colSpan={3} className="txn-totals-label">
                    Total ({totalled.length} transaction{totalled.length !== 1 ? 's' : ''})
                  </td>
                  <td className="txn-totals-amount">
                    {totalsByCurrency.map(t => (
                      <div key={t.currency}>{formatCurrencyFromMinor(t.amount, t.currency)}</div>
                    ))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="txn-pagination">
              <button
                className="txn-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Prev
              </button>
              <span className="txn-page-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="txn-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TransactionsTable;
